import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as teamsApi from '../../api/teams'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Team Create Page
 * Form to create a new team
 */
export default function TeamCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [formData, setFormData] = useState({
    uid: '',
    team_name: '',
    category: 'YOUTH',
  })

  const createMutation = useMutation({
    mutationFn: (data) => teamsApi.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['teams'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Team created successfully!', 'success')
      navigate('/teams')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to create team', 'error')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Create Team</h1>
        <p className="mt-2 text-gray-600">Fill in the details to create a new team</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <Input
                label="Team Name"
                name="team_name"
                value={formData.team_name}
                onChange={handleChange}
                required
                placeholder="e.g., U17 Boys Team"
              />

              <div>
                <label className="label">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="category"
                      value="YOUTH"
                      checked={formData.category === 'YOUTH'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      required
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Youth</p>
                      <p className="text-xs text-gray-500">For junior/youth teams</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="category"
                      value="SENIOR"
                      checked={formData.category === 'SENIOR'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      required
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Senior</p>
                      <p className="text-xs text-gray-500">For senior/adult teams</p>
                    </div>
                  </label>
                </div>
              </div>

              <Input
                label="UID (Optional)"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                placeholder="Unique identifier"
              />
            </div>
          </Card.Body>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/teams')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
          >
            Create Team
          </Button>
        </div>
      </form>
    </div>
  )
}